import React from "react";
import { Label } from "../../../../components";
import {
  Box,
  Stack,
  Typography,
  LinearProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
} from "@mui/material";
import {
  tableCellStyle,
  formatWithoutTimezone,
} from "../../../../utils/common";
import { capitalize } from "lodash";
import { useHistory } from "react-router-dom";
import { Routes } from "../../../../routes";
import { format } from "date-fns";

const KycSubmittedTable = ({
  kycList,
  paginateTable,
  setPaginateTable,
  fetchKycLoading,
}) => {
  const history = useHistory();

  const handleViewRow = (kycId) => {
    history.push({
      pathname: Routes.KycSubmittedDetail.path,
      search: `?kyc_id=${kycId}`,
    });
  };

  const handleChangePage = (event, newPage) => {
    setPaginateTable({ ...paginateTable, currentPage: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    setPaginateTable({
      ...paginateTable,
      currentPage: 0,
      pageSize: parseInt(event.target.value, 10),
    });
  };

  const createDataRow = (kyc) => {
    const formatDate = (dateString) => {
      if (!dateString) return "--";
      
      if (typeof dateString !== "string" && typeof dateString !== "number") {
        return "--";
      }
      
      try {
        let normalizedDate = String(dateString);
        if (normalizedDate.endsWith("GMT")) {
          normalizedDate = normalizedDate.replace(/GMT$/, "Z");
        }
        
        const formatted = formatWithoutTimezone(normalizedDate, "dd MMM yyyy");
        if (formatted && formatted !== "Invalid Date" && formatted !== "null" && formatted !== null) {
          return formatted;
        }
        const dateObj = new Date(normalizedDate);
        if (!isNaN(dateObj.getTime())) {
          return format(dateObj, "dd MMM yyyy");
        }
        return "--";
      } catch (error) {
        try {
          let normalizedDate = String(dateString);
          if (normalizedDate.endsWith("GMT")) {
            normalizedDate = normalizedDate.replace(/GMT$/, "Z");
          }
          const dateObj = new Date(normalizedDate);
          if (!isNaN(dateObj.getTime())) {
            return format(dateObj, "dd MMM yyyy");
          }
        } catch (e) {
          // Ignore
        }
        return "--";
      }
    };

    return {
      id: kyc?.id,
      kycId: kyc?.id,
      name: kyc?.name || "--",
      email: kyc?.email || "--",
      uniqueKey: kyc?.unique_key || "--",
      partyType: kyc?.party_type_display || (kyc?.party_type === "B" ? "Business" : kyc?.party_type === "I" ? "Individual" : "--"),
      kycReviewStatus: kyc?.kyc_review_status || "--",
      status: kyc?.status || "--",
      submittedAt: formatDate(kyc?.kyc_submitted_date),
      country: kyc?.country || "--",
      mobile: kyc?.mobile || "--",
      kycReviewDate: formatDate(kyc?.kyc_review_date),
      rawData: kyc,
    };
  };

  const tableData =
    kycList?.results?.length > 0
      ? kycList.results.map((kyc) => createDataRow(kyc))
      : [];

  const getStatusColor = (status) => {
    if (!status) return "application submitted";
    const statusLower = status.toLowerCase();
    if (statusLower === "approved" || statusLower === "active") {
      return "complete";
    } else if (statusLower === "rejected" || statusLower === "draft") {
      return "rejected";
    } else if (statusLower === "pending_review") {
      return "application submitted";
    }
    return "application submitted";
  };

  return (
    <Box
      sx={{
        boxShadow: "rgba(99, 99, 99, 0.2) 0px 0px 8px 0px",
        borderRadius: "8px",
        width: "100%",
        overflow: "hidden",
      }}
    >
      {fetchKycLoading && (
        <Box sx={{ width: "100%", position: "relative" }}>
          <LinearProgress />
        </Box>
      )}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "0 0 8px 8px",
          boxShadow: "none",
          overflowX: "auto",
        }}
      >
        <Table
          sx={{
            tableLayout: "auto",
            width: "100%",
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  backgroundColor: "#F4F4F4",
                  fontSize: "14px",
                  fontWeight: 600,
                  fontFamily: "Inter, sans-serif",
                  whiteSpace: "nowrap",
                }}
              >
                KYC ID
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "#F4F4F4",
                  fontSize: "14px",
                  fontWeight: 600,
                  fontFamily: "Inter, sans-serif",
                  whiteSpace: "nowrap",
                }}
              >
                Name
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "#F4F4F4",
                  fontSize: "14px",
                  fontWeight: 600,
                  fontFamily: "Inter, sans-serif",
                  whiteSpace: "nowrap",
                }}
              >
                Email
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "#F4F4F4",
                  fontSize: "14px",
                  fontWeight: 600,
                  fontFamily: "Inter, sans-serif",
                  whiteSpace: "nowrap",
                }}
              >
                Unique Key
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "#F4F4F4",
                  fontSize: "14px",
                  fontWeight: 600,
                  fontFamily: "Inter, sans-serif",
                  whiteSpace: "nowrap",
                }}
              >
                Party Type
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "#F4F4F4",
                  fontSize: "14px",
                  fontWeight: 600,
                  fontFamily: "Inter, sans-serif",
                  whiteSpace: "nowrap",
                }}
              >
                KYC Review Status
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "#F4F4F4",
                  fontSize: "14px",
                  fontWeight: 600,
                  fontFamily: "Inter, sans-serif",
                  whiteSpace: "nowrap",
                }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "#F4F4F4",
                  fontSize: "14px",
                  fontWeight: 600,
                  fontFamily: "Inter, sans-serif",
                  whiteSpace: "nowrap",
                }}
              >
                Submitted At
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.length === 0 && !fetchKycLoading ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No KYC Submissions Found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tableData.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                    "& td": {
                      fontWeight: 500,
                      color: "#667085",
                      fontFamily: "Inter, sans-serif",
                      fontSize: "14px",
                      py: 1.5,
                      whiteSpace: "nowrap",
                    },
                  }}
                  onClick={() => handleViewRow(row.id)}
                >
                  <TableCell>
                    <Tooltip title={row.id || "--"} arrow>
                      <Typography
                        variant="body2"
                        sx={{
                          ...tableCellStyle,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.id || "--"}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        ...tableCellStyle,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.name || "--"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={row.email || "--"} arrow>
                      <Typography
                        variant="body2"
                        sx={{
                          ...tableCellStyle,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.email || "--"}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        ...tableCellStyle,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.uniqueKey || "--"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        ...tableCellStyle,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.partyType || "--"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Label
                      variant={"ghost"}
                      color={getStatusColor(row.kycReviewStatus)}
                      sx={{ textTransform: "capitalize" }}
                    >
                      {row.kycReviewStatus?.replace(/_/g, " ") || "--"}
                    </Label>
                  </TableCell>
                  <TableCell>
                    <Label
                      variant={"ghost"}
                      color={getStatusColor(row.status)}
                      sx={{ textTransform: "capitalize" }}
                    >
                      {row.status || "--"}
                    </Label>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        ...tableCellStyle,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.submittedAt || "--"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={kycList?.count || 0}
        page={paginateTable.currentPage}
        onPageChange={handleChangePage}
        rowsPerPage={paginateTable.pageSize}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          "& .MuiTablePagination-toolbar": {
            fontFamily: "Inter, sans-serif",
          },
        }}
      />
    </Box>
  );
};

export default KycSubmittedTable;

